import { useSelector, useDispatch } from 'react-redux'
import { setUser, initialState } from 'store/auth/userSlice'
import { apiSignIn, apiCWHostedSignIn, apiValidateSignIn } from 'services/AuthService'
import {
    loginWithRedirect,
    acquireIdToken,
    getActiveAccount,
    signOut as msalSignOut,
} from 'services/AzureAuthService'
import { onSignInSuccess, onSignOutSuccess, setToken } from 'store/auth/sessionSlice'
import appConfig from 'configs/app.config'
import { REDIRECT_URL_KEY } from 'constants/app.constant'
import { useNavigate } from 'react-router-dom'
import useQuery from './useQuery'

function useAuth() {
    const dispatch = useDispatch()

    const navigate = useNavigate()

    const query = useQuery()

    const { token, signedIn } = useSelector((state) => state.auth.session)

    const signIn = async (values) => {
        try {
            const resp = await apiSignIn(values)
            if (resp.data) {
                const { token, data } = resp.data
                if (token && data.user) {
                    dispatch(onSignInSuccess(token))
                    dispatch(
                        setUser({
                            userName: data.user?.preferred_username,
                            email: data.user?.preferred_username,
                            name: data.user?.name || data.user?.preferred_username,
                            user_guid: data.user?.preferred_username,
                            first_name: data.user?.name,
                            last_name: '',
                            cwmIdentifier: data.user?.cwmIdentifier || null,
                            cwmId: data.user?.cwmId || null,
                            enabled: 1,
                            authority: data.user?.roles
                        })
                    )
                    const redirectUrl = query.get(REDIRECT_URL_KEY)
                    navigate(
                        redirectUrl ? redirectUrl : appConfig.authenticatedEntryPath
                    )
                    return {
                        status: 'success',
                        message: '',
                    }
                }
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }
    }

    const signInWithCWMHostedAPI = async (values) => {
        try {
            const resp = await apiCWHostedSignIn(values)
            if (resp.data) {
                const { token, data } = resp.data
                if (token && data.user) {
                    dispatch(setToken(token))
                    dispatch(
                        setUser({
                            userName: data.user?.preferred_username,
                            email: data.user?.preferred_username,
                            name: data.user?.name || data.user?.preferred_username,
                            user_guid: data.user?.preferred_username,
                            first_name: data.user?.name,
                            last_name: '',
                            cwmIdentifier: data.user?.cwmIdentifier || null,
                            cwmId: data.user?.cwmId || null,
                            enabled: 1,
                            authority: [] // No roles from CWM Hosted API
                        })
                    )

                    return {
                        status: 'success',
                        message: '',
                    }
                }
            }
        } catch (errors) {
            return {
                status: 'failed',
                message: errors?.response?.data?.message || errors.toString(),
            }
        }

        return { status: 'failed', message: 'Sign-in incomplete. Try again.' }
    }

    async function signInRequest(token) {
        const signInResult = await signIn({ token })
        if (signInResult.status === 'success') {
            return { status: 'success', message: '' }
        } else {
            handleSignOut()
            return { status: 'failed', message: signInResult.message }
        }
    }

    const signInWithMicrosoft = async (redirect = true) => {
        // Trigger redirect login; navigation back will be handled by MSAL
        // After redirect completes (on next load), try to acquire id token

        let idToken = await acquireIdToken()
        let account = getActiveAccount()
        if (idToken && account) {
            const aquireSignin = await signInRequest(idToken)
            if (aquireSignin.status === 'success') {
                return { status: 'success', message: '' }
            }
        }

        if (redirect) {
            // Not signed in yet; initiate login
            account = await loginWithRedirect()
            idToken = await acquireIdToken()
            if (idToken && account) {
                return await signInRequest(idToken)
            }
        }
        return { status: 'failed', message: 'Sign-in incomplete. Try again.' }
    }

    const handleSignOut = () => {
        dispatch(onSignOutSuccess())
        dispatch(setUser(initialState))
        navigate(appConfig.unAuthenticatedEntryPath)
    }

    const signOut = async () => {
        handleSignOut()
        try {
            await msalSignOut() // Redirects to Azure logout page interrupting flow
        } catch (_) {
            // ignore
        }
    }

    const validateSession = async () => {
        // Validate AAD token presence
        let idToken = await acquireIdToken()
        let account = getActiveAccount()

        if (!idToken || !account) {
            handleSignOut()
            return { valid: false }
        }

        // Validate token with backend
        if (token && signedIn) {
            try {
                // Axios will handle 401 responses, so just check for data
                const resp = await apiValidateSignIn()
                if (resp.data) {
                    return { valid: true }
                }
            } catch (_) {
                // ignore
            }
        }
        handleSignOut()
        return { valid: false }
    }

    return {
        authenticated: token && signedIn,
        signInWithMicrosoft,
        signOut,
        signInWithCWMHostedAPI,
        validateSession,
    }
}

export default useAuth
